import { AfterViewInit, Component, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { MatPaginator } from "@angular/material/paginator";
import { MatDialog } from "@angular/material/dialog";
import { CommonModule } from "@angular/common";
import { TranslateModule } from "@ngx-translate/core";
import { MatTableModule } from "@angular/material/table";
import { MatSortModule } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatCardModule } from "@angular/material/card";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { Division } from "../../core/models/division.model";
import { ApplicationDataService } from "../../core/services/application-data.service";
import { GenericCrudService } from "../../core/services/generic-crud.service";
import { AppUrlConstants } from "../../core/constants/AppUrlConstants";
import { AddEditDivisionComponent } from "./add-edit-dialog-division/add-edit-division.component";
import { DeleteDialogComponent } from "../../shared/delete-dialog/delete-dialog.component";
import { AlertDialogComponent } from "../../shared/alert-dialog/alert-dialog.component";
import { Config } from "../../core/DefaultConfig";
import { MatPaginatorIntl } from '@angular/material/paginator';
import { getCustomPaginatorIntl } from "../../shared/paginator.helper";

@Component({
  selector: "app-division",
  templateUrl: "./division.component.html",
  styleUrls: ["./division.component.css"],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    MatGridListModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
  ],
  providers: [
    { provide: MatPaginatorIntl, useValue: getCustomPaginatorIntl('Divisions per page') }
  ],
})
export class DivisionComponent implements AfterViewInit {
  displayedColumns = ["sr", "name", "description", "segmentType", "action"];
  config = new Config();
  dataSource = new MatTableDataSource<Division>([]);
  dataList: Division[] = [];
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    private dialog: MatDialog,
    private appDataService: ApplicationDataService,
    private service: GenericCrudService
  ) { }

  ngAfterViewInit(): void {
    this.loadDivisions();
  }

  private loadDivisions(): void {
    this.appDataService.getByType("DIVISION").subscribe({
      next: (res: any) => {
        this.dataList = res.data;
        this.dataSource.data = this.dataList;
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      },
      error: (err) => console.error("Failed to load divisions", err),
    });
  }

  // Check if export should be disabled
  get isExportDisabled(): boolean {
    return !this.dataSource.filteredData || this.dataSource.filteredData.length === 0;
  }

  openAlert(message: string) {
    const dialogRef = this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: "OK",
        },
      },
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  add(): void {
    const ref = this.dialog.open(AddEditDivisionComponent);
    ref.afterClosed().subscribe((saved) => {
      if (saved) this.loadDivisions();
    });
  }

  edit(row: Division, index: number): void {
    const ref = this.dialog.open(AddEditDivisionComponent, { data: row });
    ref.afterClosed().subscribe((updated) => {
      if (updated) this.loadDivisions();
    });
  }

  delete(adminStrId: number): void {
    const ref = this.dialog.open(DeleteDialogComponent, {
      data: {
        title: "DROP_DIVISION_TITLE",
        message: "DROP_DIVISION_MESSAGE",
        deleteButtonText: "DROP",
        cancelButtonText: "CANCEL",
      },
    });
    ref.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.service
          .delete(adminStrId, AppUrlConstants.ADMINISTRATIVE_STRUCTURE_URL)
          .subscribe({
            next: () => this.loadDivisions(),
            error: (err) => console.error("Delete failed", err),
          });
      }
    });
  }

  async downloadAllAsPdf(scope: "all" | "current"): Promise<void> {
    // Check if export should be disabled
    if (this.isExportDisabled) {
      this.openAlert("No data available to export");
      return;
    }

    let list: Division[];
    if (scope === "all") {
      // Get properly sorted and filtered data
      list = this.getSortedData(this.dataSource.filteredData);
    } else {
      // Get only the current page data (sorted and filtered)
      const sortedData = this.getSortedData(this.dataSource.filteredData);
      const start = this.paginator.pageIndex * this.paginator.pageSize;
      const end = start + this.paginator.pageSize;
      list = sortedData.slice(start, end);
    }

    // Create a printable HTML document with proper RTL support
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      this.openAlert("Please allow pop-ups to generate the PDF");
      return;
    }

    // Write the HTML content with proper RTL support and logos
    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Divisions List</title>
      <meta charset="UTF-8">
      <style>
        @import url('https://fonts.googleapis.com/earlyaccess/notonastaliqurdu.css');
          
        @page {
          size: landscape;
        }

        body {
          font-family: Arial, sans-serif;
          padding: 20px;
        }
        
        h1 {
          text-align: center;
          margin-bottom: 30px;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        
        td {
          border: 1px solid #ddd;
          padding: 8px;
          font-size: 14px;
          text-align: center;
          font-weight: normal;
        }
        th {
          background-color: #00a65a;
          color: white;
          border: 1px solid #ddd;
          padding: 8px;
          text-align: center;
          font-weight: bold;
          font-size: 16px;
        }
        
        .urdu-text {
          font-family: 'Noto Nastaliq Urdu', serif;
          direction: rtl;
          text-align: right;
        }
        
        .center-text {
          text-align: center;
        }
        
        .logo-container {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          margin-bottom: 20px;
        }
        .logo-container img {
          margin-right: 15px;
        }
        .logo-container img:first-child {
          height: 80px;
          width: auto;
        }
        .logo-container img:last-child {
          height: 50px;
          width: auto;
        }
        
        @media print {
          button {
            display: none;
          }
        }
      </style>
    </head>
    <body>
      <div class="logo-container">
        <img src="../../../assets/img/logo.png" alt="Logo">
        <img src="../../../assets/img/pulselogo.png" alt="Pulse Logo">
      </div>
      <h1>Divisions List</h1>
      <table>
        <thead>
          <tr>
            <th class="center-text">Sr. No/<span class="urdu-text">سیریل نمبر</span></th>
            <th>Division Name/<span class="urdu-text">ڈویژن کا نام</span></th>
            <th>Description/<span class="urdu-text">تفصیل</span></th>
            <th class="center-text">Segment Type/<span class="urdu-text">سیگمنٹ کی قسم</span></th>
          </tr>
        </thead>
        <tbody>
          ${list
        .map(
          (item, index) => `
            <tr>
              <td class="center-text">${index + 1}</td>
              <td><span class="urdu-text">${item.name || ''}</span></td>
              <td><span class="urdu-text">${item.description || ''}</span></td>
              <td class="center-text">${item.segmentType || ''}</td>
            </tr>
          `
        )
        .join("")}
        </tbody>
      </table>
      <script>
        // Auto-print when fonts are loaded
        document.fonts.ready.then(() => {
          setTimeout(() => {
            window.print();
          }, 500);
        });
      </script>
    </body>
    </html>
  `);

    printWindow.document.close();
  }

  /** Export current or all divisions to CSV **/
  downloadAllAsExcel(scope: "all" | "current"): void {
    // Check if export should be disabled
    if (this.isExportDisabled) {
      this.openAlert("No data available to export");
      return;
    }

    let list: Division[];
    if (scope === "all") {
      // Get properly sorted and filtered data
      list = this.getSortedData(this.dataSource.filteredData);
    } else {
      // Get only the current page data (sorted and filtered)
      const sortedData = this.getSortedData(this.dataSource.filteredData);
      const start = this.paginator.pageIndex * this.paginator.pageSize;
      const end = start + this.paginator.pageSize;
      list = sortedData.slice(start, end);
    }

    const headers = ["Sr. No", "Name", "Description", "Segment Type"];

    // Add BOM (Byte Order Mark) for UTF-8 encoding
    const BOM = "\uFEFF";

    const csvRows = [
      headers.join(","),
      ...list.map((d, i) =>
        [
          i + 1,
          `"${(d.name || "").replace(/"/g, '""')}"`,
          `"${(d.description || "").replace(/"/g, '""')}"`,
          `"${d.segmentType || ""}"`,
        ].join(",")
      ),
    ];

    // Add BOM to ensure proper UTF-8 encoding for Urdu characters
    const csvContent = BOM + csvRows.join("\r\n");

    // Set explicit UTF-8 encoding in the blob
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `divisions-${scope}.csv`);
    link.click();
  }

  // Helper method to get properly sorted data
  private getSortedData(data: Division[]): Division[] {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.slice().sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      const valueA = this.getSortValue(a, this.sort.active);
      const valueB = this.getSortValue(b, this.sort.active);

      return this.compare(valueA, valueB, isAsc);
    });
  }

  // Helper method to get sort value from object
  private getSortValue(item: Division, column: string): string | number {
    switch (column) {
      case 'name':
        return (item.name || '').toLowerCase();
      case 'description':
        return (item.description || '').toLowerCase();
      case 'segmentType':
        return (item.segmentType || '').toLowerCase();
      default:
        return '';
    }
  }

  // Helper method to compare values
  private compare(a: string | number, b: string | number, isAsc: boolean): number {
    if (a < b) {
      return isAsc ? -1 : 1;
    }
    if (a > b) {
      return isAsc ? 1 : -1;
    }
    return 0;
  }
}
